import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { submitInterest } from '../api/interest';
import styles from '../styles/InsegnantiLandingPage.module.css';
import FaIcon from './FaIcon';

// Features of the upcoming teacher portal
const PORTAL_FEATURES = [
  {
    icon: '🔍',
    title: 'Ricerca avanzata',
    description: 'Trova giochi per argomento, livello di difficoltà, competenza linguistica e obiettivo didattico.'
  },
  {
    icon: '📊',
    title: 'Livelli adattati',
    description: 'Giochi calibrati per ogni livello: dai principianti agli avanzati.'
  },
  {
    icon: '📚',
    title: 'Percorsi tematici',
    description: 'Raccolte di giochi organizzati per unità didattiche, temi grammaticali o argomenti lessicali.'
  },
  {
    icon: '📈',
    title: 'Monitoraggio progressi',
    description: 'Visualizza i progressi dei tuoi studenti e identifica le aree che necessitano maggiore attenzione.'
  },
  {
    icon: '🎯',
    title: 'Obiettivi didattici',
    description: 'Ogni gioco è collegato a obiettivi didattici chiari, facilitando la pianificazione delle lezioni.'
  },
  {
    icon: '💼',
    title: 'Risorse per la classe',
    description: 'Suggerimenti per attività in classe e guide per integrare i giochi nella didattica.'
  }
];

// Target audiences
const TARGET_AUDIENCES = [
  { emoji: '🏫', label: "Scuole primarie all'estero" },
  { emoji: '📖', label: "Scuole secondarie all'estero" },
  { emoji: '🌍', label: 'Italiano L2/LS' },
  { emoji: '🎓', label: 'Università' },
  { emoji: '👨‍🏫', label: 'Tutor privati' },
  { emoji: '💻', label: 'Corsi online' },
  { emoji: '💡', label: 'CPIA' },
];

// Use cases
const USE_CASES = [
  {
    title: 'Ripasso grammaticale',
    description: 'Usa i nostri giochi per consolidare le regole grammaticali in modo divertente e coinvolgente.',
    icon: '✏️'
  },
  {
    title: 'Ampliamento lessicale',
    description: 'Espandi il vocabolario dei tuoi studenti con sfide quotidiane adatte al loro livello.',
    icon: '📝'
  },
  {
    title: 'Compiti a casa',
    description: 'Assegna giochi specifici come compiti: sono divertenti e i risultati sono tracciabili.',
    icon: '🏠'
  }
];

const InsegnantiLandingPage = () => {
  const { isSignedIn, user } = useUser();
  
  // Form state
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null, 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Get Clerk email
  const clerkEmail = useMemo(() => {
    try {
      if (isSignedIn && user) {
        if (user.primaryEmailAddress?.emailAddress) return user.primaryEmailAddress.emailAddress;
        const first = user.emailAddresses?.[0]?.emailAddress;
        if (first) return first;
      }
    } catch (_) {}
    return null;
  }, [isSignedIn, user]);

  // Initialize email with Clerk email or localStorage
  useEffect(() => {
    const initialEmail = clerkEmail || localStorage.getItem('email') || '';
    setEmail(initialEmail);
  }, [clerkEmail]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setErrorMessage('Inserisci il tuo indirizzo email.');
      return;
    }

    setSubmitting(true);
    setStatus(null);
    setErrorMessage('');

    try {
      // Use the interest API for teacher portal interest
      await submitInterest({
        email: email.trim(),
        interest_type: 'teacher',
        clerk_user_id: user?.id || null
      });

      // Save email locally
      if (email.trim() !== localStorage.getItem('email')) {
        localStorage.setItem('email', email.trim());
      }
      
      // Mark teacher interest
      localStorage.setItem('teacher-portal-interest', Date.now().toString());
      
      setStatus('success');
    } catch (error) {
      console.error('Teacher signup error:', error);
      setStatus('error');
      if (error.response?.status === 429) {
        setErrorMessage('Hai già inviato una richiesta recentemente. Riprova più tardi.');
      } else if (error.response?.status === 400) {
        setErrorMessage(error.response?.data?.detail || 'Dati non validi. Controlla la tua email.');
      } else {
        setErrorMessage('Si è verificato un errore. Riprova più tardi.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Check if already signed up
  const isAlreadySignedUp = !!localStorage.getItem('teacher-portal-interest');
  const showSuccessState = status === 'success' || isAlreadySignedUp;

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Animated parallax background shapes */}
        <div className={styles.heroBackground}>
          <div className={`${styles.parallaxShape} ${styles.shape1}`} />
          <div className={`${styles.parallaxShape} ${styles.shape2}`} />
          <div className={`${styles.parallaxShape} ${styles.shape3}`} />
          <div className={`${styles.parallaxShape} ${styles.shape4}`} />
          <div className={`${styles.parallaxShape} ${styles.shape5}`} />
        </div>

        {/* Gradient overlay */}
        <div className={styles.heroGradient} />

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <span className={styles.badgeIcon}>🚀</span>
            <span>In arrivo</span>
          </div>

          <h1 className={styles.heroTitle}>
            Il Portale per<br />
            <span className={styles.highlight}>Insegnanti</span>
          </h1>
          
          <p className={styles.heroSubtitle}>
            Trova i giochi perfetti per i tuoi studenti. Filtra per livello, 
            argomento e obiettivo didattico. Tutto in un unico posto.
          </p>

          {showSuccessState ? (
            <div className={styles.successContainer}>
              <div className={styles.successBadge}>
                <FaIcon className={`fas fa-check-circle ${styles.successIcon}`}  />
                <div className={styles.successText}>
                  <span className={styles.successLabel}>Sei nella lista!</span>
                  <span className={styles.successEmail}>{email || localStorage.getItem('email')}</span>
                </div>
              </div>
              <p className={styles.successNote}>
                Ti contatteremo appena il portale sarà pronto. Grazie per l'interesse!
              </p>
              <Link to="/" className={styles.homeButton}>
                <FaIcon className="fas fa-gamepad"  />
                <span>Esplora i giochi</span>
              </Link>
            </div>
          ) : (
            <form className={styles.heroForm} onSubmit={handleSubmit}>
              <div className={styles.inputGroup}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setStatus(null);
                  }}
                  placeholder="La tua email professionale"
                  className={styles.emailInput}
                  disabled={submitting}
                  required
                />
                <button
                  type="submit"
                  className={`${styles.submitButton} ${submitting ? styles.submitting : ''}`}
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? (
                    <>
                      <FaIcon className="fas fa-spinner fa-spin"  />
                      <span>Invio...</span>
                    </>
                  ) : (
                    <>
                      <FaIcon className="fas fa-bell"  />
                      <span>Avvisami</span>
                    </>
                  )}
                </button>
              </div>
              
              {status === 'error' && (
                <div className={styles.errorMessage}>
                  <FaIcon className="fas fa-exclamation-circle"  />
                  <span>{errorMessage}</span>
                </div>
              )}
              
              <p className={styles.privacyNote}>
                <FaIcon className="fas fa-lock"  />
                Niente spam. Solo un avviso quando il portale sarà pronto.
              </p>
            </form>
          )}
        </div>

        <div className={styles.scrollHint}>
          <span>Scopri le funzionalità</span>
          <FaIcon className="fas fa-chevron-down"  />
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className={styles.problemSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.problemContent}>
            <div className={styles.problemIcon}>💭</div>
            <h2 className={styles.problemTitle}>Sappiamo cosa significa</h2>
            <p className={styles.problemText}>
              Trovare <em>risorse didattiche di qualità</em> per l'italiano richiede tempo. 
              Valutare se un gioco è adatto al livello dei tuoi studenti, ancora di più.
            </p>
            <p className={styles.problemText}>
              Il <strong>Portale Insegnanti</strong> nasce per risolvere questo problema: 
              un unico punto di accesso a tutti i nostri giochi, con filtri avanzati 
              per trovare esattamente quello che ti serve.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>✨</span>
            <h2 className={styles.sectionTitle}>Cosa potrai fare</h2>
            <p className={styles.sectionSubtitle}>
              Funzionalità pensate specificamente per chi insegna
            </p>
          </div>

          <div className={styles.featuresGrid}>
            {PORTAL_FEATURES.map((feature, index) => (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className={styles.audienceSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🎓</span>
            <h2 className={styles.sectionTitle}>Per chi è pensato</h2>
            <p className={styles.sectionSubtitle}>
              Che tu insegni in Italia o all'estero, c'è qualcosa per te
            </p>
          </div>

          <div className={styles.audienceCloud}>
            {TARGET_AUDIENCES.map((audience, index) => (
              <span key={index} className={styles.audienceTag}>
                <span className={styles.tagEmoji}>{audience.emoji}</span>
                <span className={styles.tagLabel}>{audience.label}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={styles.useCasesSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>💡</span>
            <h2 className={styles.sectionTitle}>Come usarlo in classe</h2>
            <p className={styles.sectionSubtitle}>
              Idee pratiche per integrare i giochi nella tua didattica
            </p>
          </div>

          <div className={styles.useCasesGrid}>
            {USE_CASES.map((useCase, index) => (
              <div key={index} className={styles.useCaseCard}>
                <div className={styles.useCaseIcon}>{useCase.icon}</div>
                <h3>{useCase.title}</h3>
                <p>{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>6</span>
              <span className={styles.statLabel}>Giochi diversi</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>∞</span>
              <span className={styles.statLabel}>Sfide all'anno</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>100%</span>
              <span className={styles.statLabel}>Gratuito</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <span className={styles.ctaEmoji}>📬</span>
          <h2 className={styles.ctaTitle}>Non perderti il lancio!</h2>
          <p className={styles.ctaText}>
            Stiamo costruendo qualcosa di speciale per gli insegnanti. 
            Iscriviti per essere tra i primi a provarlo.
          </p>

          {showSuccessState ? (
            <div className={styles.ctaSuccessContainer}>
              <div className={styles.ctaSuccessBadge}>
                <FaIcon className={`fas fa-check-circle ${styles.ctaSuccessIcon}`}  />
                <span>Grazie! Ti terremo aggiornato.</span>
              </div>
            </div>
          ) : (
            <form className={styles.ctaForm} onSubmit={handleSubmit}>
              <div className={styles.ctaInputGroup}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setStatus(null);
                  }}
                  placeholder="La tua email"
                  className={styles.ctaEmailInput}
                  disabled={submitting}
                  required
                />
                <button
                  type="submit"
                  className={`${styles.ctaSubmitButton} ${submitting ? styles.submitting : ''}`}
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? 'Invio...' : 'Iscriviti'}
                </button>
              </div>
            </form>
          )}

          <div className={styles.ctaButtons}>
            <Link to="/collaborazioni" className={styles.ctaSecondaryButton}>
              <FaIcon className="fas fa-handshake"  />
              <span>Collabora con noi</span>
            </Link>
            <Link to="/contatti" className={styles.ctaSecondaryButton}>
              <FaIcon className="fas fa-envelope"  />
              <span>Contattaci</span>
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default InsegnantiLandingPage;
